//import jsonServerProvider from "ra-data-json-server";
import simpleRestProvider from "ra-data-simple-rest";
import { fetchUtils, withLifecycleCallbacks } from "react-admin";
import { combineDataProviders } from "react-admin";
import { BASE_API_URL } from "config/configUrl";
import { getAccessToken } from "./authProvider";
import { checkSession } from "./checkSession";

const httpClient = (url: any, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }

  options.headers.set("Authorization", `Bearer ${getAccessToken()}`);
  options.credentials = "include";

  try {
    return fetchUtils.fetchJson(url, options);
  } catch (error: any) {
    console.log("error", error);
    // ⛔️ Si 401 ou 403 → essayer de refresh
    if (error.status === 401 || error.status === 403) {
      const refreshed = checkSession();

      if (refreshed) {
        // 🆕 access_token dans localStorage, on réessaye
        options.headers.set("Authorization", `Bearer ${getAccessToken()}`);
        return fetchUtils.fetchJson(url, options);
      }

      // ❌ sinon c'est une vraie déconnexion
      throw error;
    }

    throw error;
  }
};

const getResourcePath = (resource) => {
  switch (resource) {
    case "cryptoOrders":
      return "cryptopayments/orders";
    case "cryptoPayments":
      return "cryptopayments/payments";
    case "cryptoConversions":
      return "cryptopayments/conversions";
    case "cryptoOrdersPayments":
      return "cryptopayments/payments/order";
    case "cryptoOrdersConversions":
      return "cryptopayments/conversions/order";
    case "cryptoOrdersStatistics":
      return "cryptopayments/orders/statisticsByMonth";
    case "statsByProject":
    case "portfolioByProject":
      return "projects";
    case "projectsMenu":
      return "projects";
    case "accountsMenu":
      return "accounts";
    case "projectsByAccount":
      return "projects";
    case "userPassword":
      return "users/password";
    case "documentOrder":
      return "cryptopayments/orders/documents";
    case "cryptoOrdersKyts":
      return "cryptopayments/kyts";
    case "gasStationTransfers":
      return "gasstation/transfers";
    case "gasStationStatements":
      return "gasstation/statements";
    case "gasStationTransfersAudit":
      return "gasstation/transfers";
    case "cryptoOrdersAudit":
      return "cryptopayments/orders";
    case "accountsResellerClients":
      return "accounts/reseller";
    default:
      return resource;
  }
};

// Cursor paging. The API answers one page and, in X-Next, the cursor of the next one.
// The cursors met so far are kept per list (and per filter, sort and page size), so a
// page opened directly (reload, link) is reached by walking from the last known one.
type CursorState = { signature: string; cursorOf: Record<number, string | null> };
const cursorStates: Record<string, CursorState> = {};

const cursorStateOf = (resourcePath: string, params: any): CursorState => {
  const signature = JSON.stringify({ filter: params.filter || {}, sort: params.sort || null, perPage: params.pagination?.perPage || null });
  const state = cursorStates[resourcePath];
  if (state?.signature === signature) return state;
  return (cursorStates[resourcePath] = { signature, cursorOf: { 1: null } });
};

// One call to a list endpoint: its items and the cursor of the page after, if any.
const fetchListPage = async (resourcePath: string, params: any, cursor: string | null) => {
  const url = new URL(`${BASE_API_URL}/${resourcePath}`);
  url.searchParams.set("filter", JSON.stringify(params.filter || {}));
  if (params.pagination?.perPage) url.searchParams.set("limit", String(params.pagination.perPage));
  if (params.sort?.field) url.searchParams.set("sort", JSON.stringify([params.sort.field, params.sort.order]));
  if (cursor) url.searchParams.set("next", cursor);

  const response = await fetchUtils.fetchJson(url.toString(), {
    credentials: "include",
    headers: new Headers({
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
  });
  // The API writes "undefined" when there is no next page.
  const next = response.headers.get("X-Next");
  const data = Array.isArray(response.json) ? response.json : response.json?.items || [];
  return { data, next: next && next !== "undefined" && next !== "null" ? next : null };
};

const getListWithCursor = async (resourcePath: any, params: any) => {
  // No paging asked (e.g. the statistics chart): the list as the API gives it.
  if (!params.pagination) {
    const { data } = await fetchListPage(resourcePath, params, null);
    return { data, total: data.length };
  }
  const page = params.pagination.page || 1;
  const perPage = params.pagination.perPage || 10;
  const state = cursorStateOf(resourcePath, params);

  // An endpoint that answers the whole list at once is paged here.
  const whole = (data: any[]) => ({ data: data.slice((page - 1) * perPage, page * perPage), total: data.length });

  // Walk to the requested page from the closest page whose cursor is known.
  let at = page;
  while (at > 1 && state.cursorOf[at] === undefined) at -= 1;
  while (at < page) {
    const { data, next } = await fetchListPage(resourcePath, params, state.cursorOf[at]);
    if (!next && data.length > perPage) return whole(data);
    if (!next) return { data: [], total: (at - 1) * perPage + data.length };
    state.cursorOf[at + 1] = next;
    at += 1;
  }

  const { data, next } = await fetchListPage(resourcePath, params, state.cursorOf[page]);
  if (!next && data.length > perPage) return whole(data);
  if (next) state.cursorOf[page + 1] = next;
  // Exact on the last page; "more than" while a next page exists.
  return { data, total: next ? page * perPage + 1 : (page - 1) * perPage + data.length };
};

const getListOrdersByOrderIdCustom = async (resourcePath: any, params: any) => {
  const url = new URL(`${BASE_API_URL}/${resourcePath}/${params.filter.id}`);
  const response = await fetchUtils.fetchJson(url.toString(), {
    credentials: "include",
    headers: new Headers({
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
  });

  const items = response.json.items;

  return {
    data: items,
    total: items.length,
  };
};

const getAuditTrail = async (resourcePath: any, params: any) => {
  const url = new URL(`${BASE_API_URL}/${resourcePath}/${params.filter.id}/audit`);
  const response = await fetchUtils.fetchJson(url.toString(), {
    credentials: "include",
    headers: new Headers({
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
  });

  const items = response.json.items || [];

  return {
    data: items,
    total: items.length,
  };
};

// The client accounts of a reseller (admins): the API answers a plain array.
const getResellerClients = async (resourcePath: any, params: any) => {
  const url = `${BASE_API_URL}/${resourcePath}/${params.filter.id}/clients`;
  const response = await fetchUtils.fetchJson(url, {
    credentials: "include",
    headers: new Headers({ Accept: "application/json", Authorization: `Bearer ${getAccessToken()}` }),
  });
  const items = Array.isArray(response.json) ? response.json : response.json.items || [];
  return { data: items, total: items.length };
};

const createCryptoOrder = async (resourcePath, params) => {
  const url = `${BASE_API_URL}/${resourcePath}`;

  const response = await fetchUtils.fetchJson(url, {
    method: "POST",
    credentials: "include",
    headers: new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
    body: JSON.stringify(params.data),
  });

  const json = response.json;

  if (!json?.order?.id) {
    throw new Error("L'API n'a pas retourné d'identifiant dans json.order.id");
  }

  return {
    data: {
      ...json.order,
    },
  };
};

const createGasStationTransfer = async (params: any) => {
  const { projectId, internalRef, amount, currency, toAddress, blockchain, network, webhookUrl, travelRule } = params.data;

  const response = await fetchUtils.fetchJson(`${BASE_API_URL}/gasstation/transfers/admin`, {
    method: "POST",
    credentials: "include",
    headers: new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
    body: JSON.stringify({
      projectId,
      internalRef,
      amount,
      currency,
      toAddress,
      blockchain,
      network,
      webhookUrl,
      travelRule,
    }),
  });

  return {
    data: {
      ...response.json,
    },
  };
};

const simpleRestProviderWithCustomResource = (
  apiUrl: string,
  httpClient: any
) => {
  const provider = simpleRestProvider(apiUrl, httpClient);

  return {
    ...provider,
    getList: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      if (
        resource === "cryptoOrders" ||
        resource === "users" ||
        resource === "accounts" ||
        resource === "projects" ||
        resource === "cryptoConversions" ||
        resource === "paymentLinks" ||
        resource === "gasStationTransfers" ||
        resource === "gasStationStatements"
      ) {
        return getListWithCursor(resourcePath, params);
      }

      if (resource === "cryptoOrdersPayments") {
        return getListOrdersByOrderIdCustom(resourcePath, params);
      }
      if (resource === "cryptoOrdersConversions") {
        return getListOrdersByOrderIdCustom(resourcePath, params);
      }
      if (resource === "cryptoOrdersKyts") {
        return getListOrdersByOrderIdCustom(resourcePath, params);
      }
      if (resource === "cryptoOrdersAudit") {
        // react-admin needs an id per row; entries come oldest first.
        return getAuditTrail(resourcePath, params).then((result) => ({
          ...result, data: result.data.map((entry: any, index: number) => ({ id: index, ...entry })),
        }));
      }
      if (resource === "documentOrder") {
        return getListOrdersByOrderIdCustom(resourcePath, params);
      }
      if (resource === "gasStationTransfersAudit") {
        return getAuditTrail(resourcePath, params);
      }
      if (resource === "accountsResellerClients") {
        return getResellerClients(resourcePath, params);
      }
      if (resource === "cryptoOrdersStatistics") {
        params.pagination = null;
        params.sort = null;
        const response = getListWithCursor(resourcePath, params);
        return response;
      }
      return provider.getList(resourcePath, params);
    },
    getOne: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      if (resource === "statsByProject") {
        const url = new URL(
          `${BASE_API_URL}/${resourcePath}/${params.id}/stats`
        );
        return fetchUtils
          .fetchJson(url.toString(), {
            credentials: "include",
            headers: new Headers({
              Accept: "application/json",
              Authorization: `Bearer ${getAccessToken()}`,
            }),
          })
          .then((response) => ({
            data: { id: params.id, data: response.json },
          }));
      }
      if (resource === "portfolioByProject") {
        const url = new URL(
          `${BASE_API_URL}/${resourcePath}/${params.id}/portfolio`
        );
        return fetchUtils
          .fetchJson(url.toString(), {
            credentials: "include",
            headers: new Headers({
              Accept: "application/json",
              Authorization: `Bearer ${getAccessToken()}`,
            }),
          })
          .then((response) => ({
            data: { id: params.id, data: response.json },
          }));
      }
      return provider.getOne(resourcePath, params);
    },
    getMany: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      return provider.getMany(resourcePath, params);
    },
    getManyReference: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      return provider.getManyReference(resourcePath, params);
    },
    create: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      delete cursorStates[resourcePath];
      if (resource === "cryptoOrders") {
        return createCryptoOrder(resourcePath, params);
      }
      if (resource === "gasStationTransfers") {
        return createGasStationTransfer(params);
      }
      if(resource === "users"){
        return provider.create(`users/invite`, params);
      }
      return provider.create(resourcePath, params);
    },
    update: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      delete cursorStates[resourcePath];
      return provider.update(resourcePath, params);
    },
    updateMany: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      delete cursorStates[resourcePath];
      return provider.updateMany(resourcePath, params);
    },
    delete: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      delete cursorStates[resourcePath];
      return provider.delete(resourcePath, params);
    },
    deleteMany: (resource, params) => {
      const resourcePath = getResourcePath(resource);
      delete cursorStates[resourcePath];
      return provider.deleteMany(resourcePath, params);
    },
  };
};

const dataProvider1 = withLifecycleCallbacks(
  simpleRestProviderWithCustomResource(BASE_API_URL, httpClient),
  [
    {
      resource: "cryptoOrders",
      afterRead: async (record, dataProvider) => {
        record.id = record.OrderId;
        return record;
      },
    },
    {
      resource: "users",
      afterRead: async (record, dataProvider) => {
        //record.status = (record.status === "active") ? true : false;
        //record.id = record.Id;
        return record;
      },
      beforeGetOne: async (params, dataProvider) => {
        return params;
      },
    },
    {
      resource: "projects",
      afterRead: async (record, dataProvider) => {
        //record.status = (record.status === "active") ? true : false;
        //record.id = record.Id;
        return record;
      },
      beforeGetOne: async (params, dataProvider) => {
        return params;
      },
      beforeUpdate: async (params, dataProvider) => {
        delete params.data.hmacPassword;
        delete params.data.hmacSecret;
        
        // Traitement du logo
        const newPicturesLogo = [];
        if (params.data.logo && 
            params.data.logo.rawFile instanceof File) {
          newPicturesLogo.push(params.data.logo);
        }
        
        // Traitement du logoInverse
        const newPicturesLogoInverse = [];
        if (params.data.logoInverse && 
            params.data.logoInverse.rawFile instanceof File) {
          newPicturesLogoInverse.push(params.data.logoInverse);
        }
        
        // Conversion des images en base64
        const base64PicturesLogo = await Promise.all(
          newPicturesLogo.map(convertFileToBase64)
        );
        
        const base64PicturesLogoInverse = await Promise.all(
          newPicturesLogoInverse.map(convertFileToBase64)
        );
        
        // Création des objets logo et logoInverse mis à jour
        let updatedLogo = params.data.parameters.physicalPayment.logo;
        if (base64PicturesLogo.length > 0) {
          updatedLogo = {
            src: base64PicturesLogo[0],
            title: newPicturesLogo[0].title
          };
        }
        
        let updatedLogoInverse = params.data.parameters.physicalPayment.logoInverse;
        if (base64PicturesLogoInverse.length > 0) {
          updatedLogoInverse = {
            src: base64PicturesLogoInverse[0],
            title: newPicturesLogoInverse[0].title
          };
        }
        
        return {
          ...params,
          data: {
            ...params.data,
            parameters: {
              ...params.data.parameters,
              physicalPayment: {
                ...params.data.parameters.physicalPayment,
                logo: updatedLogo,
                logoInverse: updatedLogoInverse
              }
            }
          }
        };
      },
    },
  ]
);

const convertFileToBase64 = (_file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(_file.rawFile);
  });

const cacheDataProviderProxy = (dataProvider: any, duration = 5 * 60 * 1000) =>
  new Proxy(dataProvider, {
    get: (target, name: string) => (resource: any, params: any) => {
      if (name === "getMany" || name === "getList") {
        return dataProvider[name](resource, params).then((response: any) => {
          const validUntil = new Date();
          validUntil.setTime(validUntil.getTime() + duration);
          response.validUntil = validUntil;
          return response;
        });
      }
      return dataProvider[name](resource, params);
    },
  });

const dataProvider = combineDataProviders((resource) => {
  switch (resource) {
    case "projects":
    case "projectsMenu":
    case "accounts":
    case "accountsMenu":
    case "cryptoOrders":
    case "cryptoConversions":
    case "cryptoPayments":
    case "cryptoOrdersPayments":
    case "cryptoOrdersConversions":
    case "cryptoOrdersStatistics":
    case "users":
    case "kyt":
    case "statsByProject":
    case "portfolioByProject":
    case "projectsByAccount":
    case "userPassword":
    case "documentOrder":
    case "cryptoOrdersKyts":
    case "paymentLinks":
    case "gasStationTransfers":
    case "gasStationStatements":
    case "gasStationTransfersAudit":
    case "cryptoOrdersAudit":
    case "accountsResellerClients":
      return cacheDataProviderProxy(dataProvider1);
    //return dataProvider2;
    default:
      throw new Error(`Unknown resource: ${resource}`);
  }
});

export default dataProvider;
